import { CircleCheck, CircleDashed } from "lucide-react";
import { Button, Icon, Illustration, StatusDot } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { WhatsappAccount } from "../types/whatsapp.types";

const copy = keys.whatsapp;

/** The account settings the WhatsApp screens need, in the board's order. */
const CHECKS = [
  "tokenConfigured",
  "phoneNumberIdConfigured",
  "businessAccountIdConfigured",
  "appSecretConfigured",
  "webhookVerifyTokenConfigured",
] as const;

/**
 * "WhatsApp setup is incomplete": the account cannot manage templates or read
 * costs yet. Lists each setting as configured or not, says how many are left,
 * and opens the connection settings. It talks about missing settings only —
 * never about the account being "connected".
 */
export function SetupCard({
  readiness,
  onSettings,
}: {
  readiness: Partial<WhatsappAccount["readiness"]>;
  onSettings: () => void;
}) {
  const t = useTranslations();
  const left = CHECKS.filter((field) => !readiness[field]).length;
  const sendingBlocked = !readiness.tokenConfigured || !readiness.phoneNumberIdConfigured;
  return (
    <section
      className="rd-card"
      style={{ display: "flex", flexWrap: "wrap", gap: 28, alignItems: "center", padding: "28px 32px" }}
    >
      <div style={{ width: 170, height: 170, flex: "none", display: "flex" }}>
        <Illustration id="admin-whatsapp-setup" size={170} />
      </div>
      <div style={{ flex: "1 1 380px", minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{t("whatsapp.setupIncompleteTitle")}</h2>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: "var(--rd-text-secondary)", maxWidth: 520 }}>
          {t(copy.configRequired)}
          {sendingBlocked ? ` ${t("whatsapp.setupSendingBlocked")}` : null}
        </p>
        <ul
          aria-label={t(copy.connectionSettings)}
          style={{
            listStyle: "none",
            margin: "8px 0 0",
            padding: 0,
            borderRadius: 10,
            boxShadow: "inset 0 0 0 1px var(--rd-line-header)",
            overflow: "hidden",
            maxWidth: 520,
          }}
        >
          {CHECKS.map((field, index) => {
            const ok = Boolean(readiness[field]);
            return (
              <li
                key={field}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 14px",
                  borderTop: index ? "1px solid var(--rd-line-row)" : 0,
                }}
              >
                <Icon
                  icon={ok ? CircleCheck : CircleDashed}
                  size={16}
                  color={ok ? "var(--rd-green)" : "var(--rd-text-meta)"}
                />
                <span style={{ flex: 1, minWidth: 0, fontSize: 14 }}>{t(copy[field])}</span>
                <StatusDot tone={ok ? "green" : "red"}>{t(ok ? copy.ready : copy.missing)}</StatusDot>
              </li>
            );
          })}
        </ul>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          <Button variant="primary" size="sm" onClick={onSettings}>
            {t(copy.connectionSettings)}
          </Button>
          {left > 0 ? (
            <span style={{ alignSelf: "center", fontSize: 13, color: "var(--rd-text-tertiary)" }}>
              {t("whatsapp.setupSettingsLeft", { count: left, total: CHECKS.length })}
            </span>
          ) : null}
        </div>
      </div>
    </section>
  );
}
